import { Injectable } from "@angular/core";

import { ProductService } from "src/app/ui/services/pbm/product.service";
import { PagingRequest } from "./../../models/domains/paginator/paging-request";
import { NetworkProductConfigurationCluster } from "src/app/ui/models/domains/pbm/Benefit/network-product-configuration";
import { BenefitRepository } from "../../repositories/pbm/benefit.repository";
import { BenefitConfiguration } from "../../models/domains/pbm/Benefit/benefit-configuration";
import { TransactionConfiguration } from "../../models/domains/pbm/Benefit/transaction-configuration";
import { MedicalPrescriptionConfiguration } from "../../models/domains/pbm/Benefit/medical-prescription-configuration";
import { LifeCycleConfiguration } from "../../models/domains/pbm/Benefit/life-cycle-configuration";
import { ProductConfigurationCluster } from "../../models/domains/pbm/Benefit/product-configuration-cluster";
import { IMedicalPrescriptionMappingConfiguration } from "../../models/domains/pbm/Benefit/imedical-prescription-mapping-configuration";
import { MedicalPrescriptionService } from "../medical-prescription.service";
import { MedicalPrescriptionMappingType } from "../../models/domains/pbm/Benefit/medical-prescription-mapping-type";
import { MedicalPrescriptionMappingConfiguration } from "../../models/domains/pbm/Benefit/medical-prescription-mapping-configuration";
import { TherapeuticClassMedicalPrescriptionMappingConfiguration } from "../../models/domains/pbm/Benefit/therapeutic-class-medical-prescription-mapping-configuration";
import { TherapeuticClassService } from "./therapeutic-class.service";
import { TherapeuticClass } from "../../models/domains/pbm/Benefit/therapeutic-class";
import { PageableResult } from "../../models/domains/paginator/pageable-result";
import { BeneficiaryConfiguration } from "../../models/domains/pbm/Benefit/beneficiary-configuration";
import { ClustersService } from "./clusters.service";

@Injectable({ providedIn: "root" })
export class BenefitService {
  constructor(
    private benefitRepository: BenefitRepository,
    private productService: ProductService,
    private clustersService: ClustersService,
    private medicalPrescriptionService: MedicalPrescriptionService,
    private therapeuticClassService: TherapeuticClassService,
  ) {}

  public async searchCompany(fiscalNumber: string, pagingRequest: PagingRequest): Promise<PageableResult> {
    return this.benefitRepository.searchCompany(fiscalNumber, pagingRequest);
  }

  public async getAllBenefitConfiguration(fiscalNumber: string): Promise<BenefitConfiguration> {
    const benefitConfiguration = await this.findBenefitConfigurationByFiscalNumber(fiscalNumber);
    if (benefitConfiguration) {
      benefitConfiguration.setProductConfiguration(new ProductConfigurationCluster());
      benefitConfiguration.setNetworkProductConfiguration(new NetworkProductConfigurationCluster());
      benefitConfiguration.setTransactionConfiguration(new TransactionConfiguration());
      benefitConfiguration.setMedicalPrescriptionConfiguration(new MedicalPrescriptionConfiguration());
      benefitConfiguration.setBeneficiaryConfiguration(new BeneficiaryConfiguration());
      benefitConfiguration.setLifeCycleConfiguration(new LifeCycleConfiguration());

      return benefitConfiguration;
    }
    return null;
  }

  public async getAllBenefitConfigurationByFiscalNumber(fiscalNumber: string): Promise<BenefitConfiguration> {
    const benefitConfiguration = await this.findBenefitConfigurationByFiscalNumber(fiscalNumber);
    if (benefitConfiguration) {
      const productConfiguration = await this.findProductConfigurationByFiscalNumber(fiscalNumber);
      benefitConfiguration.setProductConfiguration(productConfiguration);

      return benefitConfiguration;
    }
    return null;
  }

  public async getAllBenefit(fiscalNumber: string){
    const benefitConfiguration = await this.findBenefitConfigurationByFiscalNumber(fiscalNumber);
    if (benefitConfiguration) {
      const productConfiguration = await this.findProductConfigurationByFiscalNumber(fiscalNumber);
      benefitConfiguration.setProductConfiguration(productConfiguration);

      const networkProductConfiguration = await this.findNetworkProductConfigurationByFiscalNumber(fiscalNumber);
      benefitConfiguration.setNetworkProductConfiguration(networkProductConfiguration);

      const transactionConfiguration = await this.findTransactionByFiscalNumber(fiscalNumber);
      benefitConfiguration.setTransactionConfiguration(transactionConfiguration);

      const medicalPrescriptionConfiguration = await this.findMedicalPrescriptionConfigurationByFiscalNumber(fiscalNumber);
      benefitConfiguration.setMedicalPrescriptionConfiguration(medicalPrescriptionConfiguration);

      const beneficiaryConfiguration = await this.findBeneficiaryConfiguration(fiscalNumber);
      benefitConfiguration.setBeneficiaryConfiguration(beneficiaryConfiguration);

      const lifeCycleConfiguration = await this.findLifeCycleByFiscalNumber(fiscalNumber);
      benefitConfiguration.setLifeCycleConfiguration(lifeCycleConfiguration);

      return benefitConfiguration;
    }
  }

  public async saveBenefitConfiguration(benefitConfiguration: BenefitConfiguration): Promise<any> {
    return await this.benefitRepository.saveBenefitConfiguration(benefitConfiguration);
  }

  public async findBenefitConfigurationByFiscalNumber(fiscalNumber: string): Promise<BenefitConfiguration> {
    try {
      return await this.benefitRepository.findBenefitConfigurationByFiscalNumber(fiscalNumber);
    } catch {
      return null;
    }
  }

  public async saveProductConfiguration(productConfiguration: ProductConfigurationCluster): Promise<any> {
    return await this.benefitRepository.saveProductConfiguration(productConfiguration);
  }

  public async findProductConfigurationByFiscalNumber(fiscalNumber: string): Promise<ProductConfigurationCluster> {
    try {
      const productConfiguration = await this.benefitRepository.findProductConfigurationByFiscalNumber(fiscalNumber);
      const productTypeList = await this.productService.getProductTypes();
      const clustersTypeList = await this.clustersService.getAllClusters();

      for (const configuration of productConfiguration.getProductConfiguration()) {
        const productType = productTypeList.find((i) => i.getKey() === configuration.getProductType().getKey());
        configuration.setProductType(productType);
        if (configuration.getProductSubType().getKey()) {
          const productSubtypes = await this.productService.getProductSubtypeByType(configuration.getProductType().getKey());
          const productSubtype = productSubtypes.length === 0 ? null : productSubtypes.find((i) => i.getKey() === configuration.getProductSubType().getKey());
          configuration.setProductSubType(productSubtype);
        } else {
          configuration.getProductSubType().setValue("Todos");
        }
      }
      for (const configuration of productConfiguration.getClustersForManagement()) {
        const clusterType = clustersTypeList.find((x) => x.Key == configuration.getClusterId());
        configuration.setClusterType(clusterType);
      }

      return productConfiguration;
    } catch {
      return null;
    }
  }

  public async saveNetworkProductConfiguration(networkConfiguration: NetworkProductConfigurationCluster): Promise<any> {
    return await this.benefitRepository.saveNetworkProductConfiguration(networkConfiguration);
  }

  public async findNetworkProductConfigurationByFiscalNumber(fiscalNumber: string): Promise<NetworkProductConfigurationCluster> {
    try {
      const networkProductConfiguration = await this.benefitRepository.findNetworkProductConfigurationByFiscalNumber(fiscalNumber);      
      return networkProductConfiguration;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async saveTransactionConfiguration(transactionConfiguration: TransactionConfiguration): Promise<any> {
    return await this.benefitRepository.saveTransactionConfiguration(transactionConfiguration);
  }

  public async findTransactionByFiscalNumber(fiscalNumber: string): Promise<TransactionConfiguration> {
    try {
      return await this.benefitRepository.findTransactionByFiscalNumber(fiscalNumber);
    } catch {
      return null;
    }
  }

  public async saveMedicalPrescriptionConfiguration(medicalPrescriptionConfiguration: MedicalPrescriptionConfiguration): Promise<any> {
    return await this.benefitRepository.saveMedicalPrescriptionConfiguration(medicalPrescriptionConfiguration);
  }

  public async findMedicalPrescriptionConfigurationByFiscalNumber(fiscalNumber: string): Promise<MedicalPrescriptionConfiguration> {
    try {
      const medicalPrescriptionConfiguration = await this.benefitRepository.findMedicalPrescriptionConfigurationByFiscalNumber(fiscalNumber);
      medicalPrescriptionConfiguration.getMedicalPrescriptionMappingConfiguration().map(async (item) => {
        this.fetchMedicalPrescriptionValidity(item);
        if (item.getType() === MedicalPrescriptionMappingType.ProductCategory) {
          await this.fetchProductCategory(item as MedicalPrescriptionMappingConfiguration);
        } else if (item.getType() === MedicalPrescriptionMappingType.TherapeuticClass) {
          await this.fetchTherapeuticClass(item as TherapeuticClassMedicalPrescriptionMappingConfiguration);
        }
      });
      return medicalPrescriptionConfiguration;
    } catch {
      return null;
    }
  }

  private async fetchMedicalPrescriptionValidity(configuration: IMedicalPrescriptionMappingConfiguration) {
    const validityList = await this.medicalPrescriptionService.getMedicalPrescriptionValidityList();
    validityList.map((item) => {
      if (item.getKey() === configuration.getMedicalPrescriptionValidity().getKey()) {
        configuration.setMedicalPrescriptionValidity(item);
      }
    });
  }

  private async fetchProductCategory(configuration: MedicalPrescriptionMappingConfiguration) {
    const productCategoryList = await this.productService.getProductCategoryList();
    productCategoryList.map((item) => {
      if (item.getKey() === configuration.getProductCategory().getKey()) {
        configuration.setProductCategory(item);
      }
    });
  }

  private async fetchTherapeuticClass(configuration: TherapeuticClassMedicalPrescriptionMappingConfiguration) {
    const pagingRequest = new PagingRequest();
    pagingRequest.setStart(0);
    pagingRequest.setEnd(1);
    pagingRequest.setFilterText(configuration.getTherapeuticClass().getDescription());
    const pageableResult = await this.therapeuticClassService.searchTherapeuticClassProductsBy(pagingRequest);
    pageableResult.getItems().map((item: TherapeuticClass) => {
      configuration.setTherapeuticClass(item);
    });
  }

  public async findBeneficiaryConfiguration(fiscalNumber: string): Promise<BeneficiaryConfiguration> {
    try {
      return await this.benefitRepository.findBeneficiaryConfiguration(fiscalNumber);
    } catch {
      return null;
    }
  }

  public async saveBeneficiaryConfiguration(beneficiaryConfiguration: BeneficiaryConfiguration): Promise<any> {
    return await this.benefitRepository.saveBeneficiaryConfiguration(beneficiaryConfiguration);
  }

  public async saveLifeCycleConfiguration(lifeCycleConfiguration: LifeCycleConfiguration): Promise<any> {
    return await this.benefitRepository.saveLifeCycleConfiguration(lifeCycleConfiguration);
  }

  public async findLifeCycleByFiscalNumber(fiscalNumber: string): Promise<LifeCycleConfiguration> {
    try {
      return await this.benefitRepository.findLifeCycleByFiscalNumber(fiscalNumber);
    } catch {
      return null;
    }
  }

  public async exportPDF(fiscalNumber: string): Promise<any> {
    return await this.benefitRepository.exportPDF(fiscalNumber);
  }
}
